import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { forIPad } from '../../utility/styled';

import type { ReactNode } from 'react';

/** Content width of a flex row: its children plus the gaps between them. */
function contentWidth(section: Element | null): number {
  if (!section) return 0;
  const children = Array.from(section.children);
  const gap = parseFloat(getComputedStyle(section).columnGap) || 0;
  const widths = children.reduce(
    (sum, child) => sum + child.getBoundingClientRect().width,
    0,
  );
  return widths + gap * Math.max(0, children.length - 1);
}

/**
 * The transport row under the canvas: one line, centred on the canvas. It
 * matches the canvas width (`canvasWidth`, from `useRendererRect`) when the
 * canvas is wide enough, and otherwise stays at the one-line width and
 * overhangs the canvas evenly — at 1440×900 and 1280×800 the canvas is
 * narrower than the controls. Only a stage narrower than one line wraps the
 * outer groups under the transport cluster. The need is measured from the
 * DOM, so nothing here has to know how wide a button or a timecode is.
 */
function BottomItems({
  canvasWidth = 0,
  children,
}: {
  /** Rendered canvas width in px; 0 (unknown) falls back to 80% of the stage. */
  canvasWidth?: number;
  children: ReactNode;
}) {
  const bottomItemsRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [needs, setNeeds] = useState({ oneLine: 0 });

  useEffect(() => {
    const root = bottomItemsRef.current;
    if (!root) return;
    const measure = () => {
      setWidth(root.getBoundingClientRect().width);
      const left = contentWidth(root.querySelector('.bottom-items-left'));
      const mid = contentWidth(root.querySelector('.bottom-items-mid'));
      const right = contentWidth(root.querySelector('.bottom-items-right'));
      // One line keeps the transport cluster centred, so both outer groups
      // need the room of the wider one.
      setNeeds({ oneLine: mid + 2 * Math.max(left, right) });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    root
      .querySelectorAll(
        '.bottom-items-left, .bottom-items-mid, .bottom-items-right',
      )
      .forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <Wrapper
      ref={bottomItemsRef}
      canvasWidth={canvasWidth}
      minWidth={needs.oneLine}
      wrapped={width > 0 && needs.oneLine > 0 && width < needs.oneLine - 1}
    >
      {children}
    </Wrapper>
  );
}

const Wrapper = styled.div<{
  canvasWidth: number;
  minWidth: number;
  wrapped: boolean;
}>`
  width: 80%;
  display: flex;
  align-items: center;
  z-index: 1;

  flex-shrink: 0;
  margin: 12px auto 16px;

  ${forIPad(`
    width: 95%;
  `)}

  /* Under the canvas: its width, but never narrower than one line of
     controls and never wider than the stage. */
  ${({ canvasWidth, minWidth }) =>
    canvasWidth > 0 &&
    css`
      width: min(100%, max(${canvasWidth}px, ${minWidth}px));
    `}

  ${({ wrapped }) =>
    wrapped &&
    css`
      flex-wrap: wrap;

      .bottom-items-left {
        order: 2;
      }
      .bottom-items-mid {
        order: 1;
        flex-basis: 100%;
        margin-bottom: 8px;
      }
      .bottom-items-right {
        order: 3;
      }
    `}
`;

const BottomItemsLeft = styled.div.attrs({ className: 'bottom-items-left' })`
  display: flex;
  justify-content: flex-start;
  flex: 1;
  gap: 2px;
`;

/* Content-sized so it stays centred: the outer groups split what is left. */
const BottomItemsMid = styled.div.attrs({ className: 'bottom-items-mid' })`
  display: flex;
  justify-content: center;
  gap: 8px;
  align-items: center;
  flex: 0 0 auto;
  position: relative;
`;

const BottomItemsRight = styled.div.attrs({ className: 'bottom-items-right' })`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex: 1;
  gap: 8px;
`;

export { BottomItems, BottomItemsLeft, BottomItemsMid, BottomItemsRight };
